/**
 * Gift Threshold Manager
 *   Automatically adds/removes gift products from cart based on cart total thresholds
 *
 * Version:
 *   1.1.0 - 2026/03/03
 */

import { cartAdd, cartGetCurrent, cartUpdate } from "@process-creative/slate-theme-tools";
import type { CartItem, GiftTier } from '../types/cart';

const SELECTOR_GIFT_CONFIG = '[data-gift-config]';

// Global flag to prevent concurrent gift operations across multiple instances
let globalGiftProcessing = false;

export class GiftThresholdManager {
  private container: HTMLElement;
  private giftTiers: GiftTier[] = [];
  private isProcessing: boolean = false;
  private debounceTimeout: NodeJS.Timeout | null = null;
  private static readonly DISMISSED_KEY_PREFIX = 'gift-dismissed-';
  private static readonly DEBOUNCE_DELAY = 300; // ms

  constructor(container: HTMLElement) {
    this.container = container;
    this.loadGiftConfig();
  }

  /**
   * Check if a gift has been dismissed by the customer in this session
   */
  private isGiftDismissed(productId: number, threshold: number): boolean {
    const key = `${GiftThresholdManager.DISMISSED_KEY_PREFIX}${productId}-${threshold}`;
    return sessionStorage.getItem(key) === 'true';
  }

  /**
   * Mark a gift as dismissed by the customer
   */
  public static markGiftDismissed(productId: number, threshold: number): void {
    const key = `${GiftThresholdManager.DISMISSED_KEY_PREFIX}${productId}-${threshold}`;
    sessionStorage.setItem(key, 'true');
    console.log('[Gift Manager] Gift dismissed:', productId, 'threshold:', threshold);
  }

  /**
   * Clear dismissal flag (e.g., when cart goes below threshold)
   */
  private clearGiftDismissal(productId: number, threshold: number): void {
    const key = `${GiftThresholdManager.DISMISSED_KEY_PREFIX}${productId}-${threshold}`;
    sessionStorage.removeItem(key);
  }

  private loadGiftConfig(): void {
    const configElement = this.container.querySelector<HTMLScriptElement>(SELECTOR_GIFT_CONFIG);
    
    if (!configElement || !configElement.textContent) {
      console.log('[Gift Manager] No gift config found');
      this.giftTiers = [];
      return;
    }

    try {
      this.giftTiers = JSON.parse(configElement.textContent);
      console.log('[Gift Manager] Loaded gift config:', this.giftTiers);
    } catch (error) {
      console.error('Failed to parse gift config:', error);
      this.giftTiers = [];
    }
  }

  /**
   * Debounced check and update method - prevents rapid successive calls
   */
  public checkAndUpdateGifts(): void {
    // Clear any pending debounce
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    // Set new debounce timeout
    this.debounceTimeout = setTimeout(() => {
      this.performGiftCheck();
    }, GiftThresholdManager.DEBOUNCE_DELAY);
  }

  /**
   * Actual gift check logic - now protected from concurrent execution
   */
  private async performGiftCheck(): Promise<void> {
    // Prevent concurrent processing at both instance and global level
    if (this.isProcessing || globalGiftProcessing || this.giftTiers.length === 0) {
      console.log('[Gift Manager] Skipping check - instance processing:', this.isProcessing, 'global:', globalGiftProcessing, 'tiers:', this.giftTiers.length);
      return;
    }

    this.isProcessing = true;
    globalGiftProcessing = true;
    console.log('[Gift Manager] Starting gift check...');

    try {
      // Reload config in case cart was re-rendered
      this.loadGiftConfig();

      const cart = cartGetCurrent();
      const cartItems: CartItem[] = cart.items || [];

      // Get all gift items currently in cart
      const giftItemsInCart = cartItems.filter(item => 
        item.properties?._gift === 'true'
      );

      // Calculate cart total EXCLUDING gift items
      // This prevents the issue where gift discounts affect threshold calculations
      const nonGiftItems = cartItems.filter(item => item.properties?._gift !== 'true');
      const cartTotalExcludingGifts = nonGiftItems.reduce((total, item) => {
        return total + (item.final_line_price || 0);
      }, 0) / 100; // Convert cents to dollars

      // Determine which gifts should be in cart based on thresholds
      const giftsToAdd: GiftTier[] = [];
      const giftsToRemove: CartItem[] = [];

      // Check each configured gift tier
      for (const tier of this.giftTiers) {
        const shouldHaveGift = cartTotalExcludingGifts >= tier.threshold;
        const existingGift = giftItemsInCart.find(item => 
          item.properties?._threshold === tier.threshold.toString() &&
          item.properties?._product_id === tier.productId.toString()
        );
        
        const isDismissed = this.isGiftDismissed(tier.productId, tier.threshold);

        if (shouldHaveGift && !existingGift && !isDismissed) {
          // Need to add this gift (only if not dismissed by customer)
          giftsToAdd.push(tier);
        } else if (!shouldHaveGift && existingGift) {
          // Need to remove this gift (cart dropped below threshold)
          giftsToRemove.push(existingGift);
          // Clear dismissal flag since they're no longer eligible anyway
          this.clearGiftDismissal(tier.productId, tier.threshold);
        }
      }

      // Remove gifts first (in case we're at a threshold boundary)
      if (giftsToRemove.length > 0) {
        await this.removeGifts(giftsToRemove);
      }

      // Add gifts
      if (giftsToAdd.length > 0) {
        await this.addGifts(giftsToAdd);
      }

    } catch (error) {
      console.error('Error checking gift thresholds:', error);
    } finally {
      this.isProcessing = false;
      globalGiftProcessing = false;
    }
  }

  private async addGifts(gifts: GiftTier[]): Promise<void> {
    const items = gifts.map(tier => ({
      id: tier.variantId,
      quantity: 1,
      properties: {
        '_gift': 'true',
        '_threshold': tier.threshold.toString(),
        '_product_id': tier.productId.toString(),
        '_auto_added': 'true'
      }
    }));

    try {
      await cartAdd({ items });
      console.log('[Gift Manager] Added gifts:', items.length);
    } catch (error) {
      console.error('Failed to add gift products:', error);
    }
  }

  private async removeGifts(gifts: CartItem[]): Promise<void> {
    const updates: { [key: string]: number } = {};
    
    gifts.forEach(item => {
      updates[item.key] = 0;
    });

    try {
      await cartUpdate({ updates });
      console.log('[Gift Manager] Removed gifts:', Object.keys(updates).length);
    } catch (error) {
      console.error('Failed to remove gift products:', error);
    }
  }

  public destroy(): void {
    // Clear any pending debounce timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }
    this.giftTiers = [];
    this.isProcessing = false;
    console.log('[Gift Manager] Destroyed');
  }
}